/**
 * Utilities de formato argentino
 */

// Moneda ARS: $193.291,83
export function formatCurrency(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '-';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(value);
}

/**
 * Parsea un string de fecha a Date SIN corrimiento de zona horaria (#491).
 *
 * Un string date-only (`YYYY-MM-DD`) que se pasa a `new Date(str)` se
 * interpreta como medianoche UTC; al mostrarlo en Argentina (UTC-3) retrocede
 * un día (2026-08-31 -> se ve 30/08). Para date-only construimos la fecha con
 * las partes en hora LOCAL, así el día no se corre. Los timestamps completos
 * (con hora/offset) se parsean normal (ya funcionan bien).
 */
function parseFechaLocal(date: string | Date): Date {
  if (typeof date !== 'string') return date;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return new Date(date);
}

/**
 * Fecha de HOY en hora LOCAL como 'YYYY-MM-DD' (#491).
 *
 * Reemplaza a `new Date().toISOString().slice(0, 10)`, que devuelve la fecha
 * en UTC y, cerca de la medianoche en Argentina (UTC-3), guarda el día
 * siguiente. Este helper usa los getters locales para no correr el día.
 */
export function todayLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Fecha: DD/MM/YYYY
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = parseFechaLocal(date);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('es-AR').format(d);
}

// Fecha + hora: DD/MM/YYYY HH:mm
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(d);
}

// CUIT/CUIL: XX-XXXXXXXX-X
export function formatCuit(cuit: string | null | undefined): string {
  if (!cuit) return '-';
  const clean = cuit.replace(/[^0-9]/g, '');
  if (clean.length !== 11) return cuit;
  return `${clean.slice(0, 2)}-${clean.slice(2, 10)}-${clean.slice(10)}`;
}

// Solo digitos (util para APIs de Pronto Pago que reciben el CUIT sin guiones)
export function cuitDigits(cuit: string | null | undefined): string {
  if (!cuit) return '';
  return cuit.replace(/[^0-9]/g, '');
}

// El login es por CUIT. La identidad real en Supabase Auth es un email SINTETICO
// `<cuit>@atsa.interno` (el usuario nunca lo ve ni lo tipea). Esto permite que el
// correo real sea un dato de contacto no unico. Ver migration 20260929000001.
export const LOGIN_EMAIL_DOMAIN = 'atsa.interno';
export function cuitToLoginEmail(cuit: string): string {
  return `${cuitDigits(cuit)}@${LOGIN_EMAIL_DOMAIN}`;
}

// Periodo YYYY-MM -> "Marzo 2026"
const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
export function formatPeriodo(periodo: string | null | undefined): string {
  if (!periodo) return '-';
  const [yyyy, mm] = periodo.split('-');
  const mesIdx = parseInt(mm, 10) - 1;
  if (isNaN(mesIdx) || mesIdx < 0 || mesIdx > 11) return periodo;
  return `${MESES[mesIdx]} ${yyyy}`;
}
